<template>
  <el-dialog 
    title="批量价格配置" 
    :visible.sync="dialogVisible" 
    width="600px"
    :before-close="handleClose"
  >
    <div class="batch-config-form">
      <!-- 保留位数选择 -->
      <div class="form-item">
        <label class="form-label" style="margin-bottom: 10px;">保留位数：</label>
        <div class="precision-selector">
          <div 
            v-for="option in precisionOptions" 
            :key="option.value"
            class="precision-option"
            :class="{ active: selectedPrecision === option.value }"
            @click="selectedPrecision = option.value"
          >
            <!-- <i class="el-icon-arrow-up precision-arrow" @click.stop="changePrecision(option.value)"></i> -->
            <span class="precision-label">{{ option.label }}</span>
            <!-- <i class="el-icon-arrow-down precision-arrow" @click.stop="changePrecision(option.value)"></i> -->
          </div>
        </div>
      </div>

      <!-- 配置方式选择 -->
      <div class="form-item">
        <label class="form-label">配置方式：</label>
        <el-radio-group v-model="configMode" @change="onConfigModeChange">
          <el-radio label="auto" :disabled="isMultipleCurrency">自动获取汇率</el-radio>
          <el-radio label="manual" :disabled="isMultipleCurrency">手动填写汇率</el-radio>
        </el-radio-group>
        <div v-if="isMultipleCurrency" class="tip-text">
          <i class="el-icon-warning"></i>
          选择多个币种时，只能使用自动获取汇率（不支持统一手动汇率）
        </div>
      </div>

      <!-- 手动填写汇率详情 -->
      <div v-if="configMode === 'manual' && !isMultipleCurrency" class="manual-config">
        <div class="currency-info">
          <div class="info-row">
            <span class="info-label">币种：</span>
            <span class="info-value">{{ currencyInfo.currency }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">美元价格参考：</span>
            <span class="info-value">${{ selectedItems[0]?.usPrice || '0' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">当前汇率：</span>
            <span class="info-value">{{ currencyInfo.queryRate }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">自动计算价格：</span>
            <span class="info-value">{{ calculateAutoPrice() }}</span>
          </div>
        </div>
        
        <div class="manual-rate-input">
          <label class="form-label">手动汇率：</label>
          <el-input
            v-model="manualPrice"
            placeholder="请输入汇率（最多4位小数）"
            style="width: 200px;"
            @input="validateManualPrice"
          />
          <div v-if="priceError" class="error-text">{{ priceError }}</div>
          <div class="tip-text" style="margin-top: 8px;">
            <i class="el-icon-info"></i>
            所有选中的配置项将使用相同的汇率计算本地价格
          </div>
        </div>
      </div>

      <!-- 选中数据预览 -->
      <div class="selected-preview">
        <div class="preview-header">
          <span>选中数据预览（{{ selectedItems.length }} 条）</span>
        </div>
        <div class="preview-list">
          <div 
            v-for="item in selectedItems" 
            :key="item.id"
            class="preview-item"
          >
            <span>{{ item.countryCurrency }} - ${{ item.usPrice }}</span>
            <span class="arrow">→</span>
            <span class="new-price">{{ calculateNewPrice(item) }}</span>
          </div>
          <!-- <div v-if="selectedItems.length > 3" class="more-text">
            还有 {{ selectedItems.length - 3 }} 条数据...
          </div> -->
        </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">
        确定配置
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'BatchPriceConfig',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialogVisible: false,
      selectedPrecision: 0, // 0=个位, 1=1位小数, 2=2位小数, -1=十位, -2=百位, -3=千位
      configMode: 'auto', // auto=自动获取, manual=手动填写汇率
      manualPrice: '', // 手动输入的汇率
      priceError: '',
      submitting: false,
      precisionOptions: [
        { label: '千位', value: -3 },
        { label: '百位', value: -2 },
        { label: '十位', value: -1 },
        { label: '个位', value: 0 },
        { label: '1位小数', value: 1 },
        { label: '2位小数', value: 2 }
      ]
    }
  },
  computed: {
    // 是否选择了多个币种
    isMultipleCurrency() {
      const currencies = [...new Set(this.selectedItems.map(item => item.countryCurrency))]
      return currencies.length > 1
    },
    
    // 单币种时的货币信息
    currencyInfo() {
      if (this.isMultipleCurrency || this.selectedItems.length === 0) {
        return {}
      }
      const firstItem = this.selectedItems[0]
      return {
        currency: firstItem.countryCurrency,
        queryRate: firstItem.queryRate || '1.00'
      }
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal) {
        this.resetForm()
      }
    },
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  methods: {
    // 重置表单
    resetForm() {
      this.selectedPrecision = 0
      this.configMode = this.isMultipleCurrency ? 'auto' : 'auto'
      this.manualPrice = ''
      this.priceError = ''
      this.submitting = false
    },
    
    // 改变精度选择
    changePrecision(value) {
      this.selectedPrecision = value
    },
    
    // 配置方式改变
    onConfigModeChange(value) {
      if (value === 'manual' && !this.isMultipleCurrency) {
        // 设置默认的手动汇率为当前汇率
        this.manualPrice = this.currencyInfo.queryRate || '1.00'
      }
    },
    
    // 计算自动价格（用于显示参考）
    calculateAutoPrice() {
      if (!this.selectedItems.length || this.isMultipleCurrency) return '0'
      
      const firstItem = this.selectedItems[0]
      const rate = parseFloat(firstItem.queryRate) || 1
      const basePrice = parseFloat(firstItem.usPrice) * rate
      
      // 根据精度四舍五入
      let price = basePrice
      if (this.selectedPrecision >= 0) {
        price = parseFloat(basePrice.toFixed(this.selectedPrecision))
      } else {
        const factor = Math.pow(10, Math.abs(this.selectedPrecision))
        price = Math.round(basePrice / factor) * factor
      }
      
      return this.formatPrice(price)
    },
    
    // 验证手动汇率输入
    validateManualPrice() {
      const value = this.manualPrice.trim()
      if (!value) {
        this.priceError = '请输入汇率'
        return false
      }
      
      const regex = /^\d+(\.\d{1,4})?$/
      if (!regex.test(value)) {
        this.priceError = '请输入有效的数字（最多4位小数）'
        return false
      }
      
      const numValue = parseFloat(value)
      if (numValue <= 0) {
        this.priceError = '汇率必须大于0'
        return false
      }
      
      this.priceError = ''
      return true
    },
    
    // 计算新价格（预览用）
    calculateNewPrice(item) {
      let newPrice
      
      if (this.configMode === 'manual' && !this.isMultipleCurrency) {
        // 手动模式：使用手动输入的汇率计算价格
        const manualRate = parseFloat(this.manualPrice) || 1
        newPrice = parseFloat(item.usPrice) * manualRate
      } else {
        // 自动模式：使用汇率计算
        const rate = parseFloat(item.queryRate) || 1
        newPrice = parseFloat(item.usPrice) * rate
      }
      
      // 根据精度四舍五入
      if (this.selectedPrecision >= 0) {
        newPrice = parseFloat(newPrice.toFixed(this.selectedPrecision))
      } else {
        const factor = Math.pow(10, Math.abs(this.selectedPrecision))
        newPrice = Math.round(newPrice / factor) * factor
      }
      
      return this.formatPrice(newPrice)
    },
    
    // 格式化价格显示
    formatPrice(price) {
      if (this.selectedPrecision > 0) {
        return price.toFixed(this.selectedPrecision)
      } else if (this.selectedPrecision === 0) {
        return Math.round(price).toString()
      } else {
        return price.toString()
      }
    },
    
    // 提交配置
    async handleSubmit() {
      // 验证手动汇率
      if (this.configMode === 'manual' && !this.isMultipleCurrency) {
        if (!this.validateManualPrice()) {
          return
        }
      }
      
      this.submitting = true
      
      try {
        // 计算所有选中项的新价格
        const priceList = this.selectedItems.map(item => {
          let newPrice
          
          if (this.configMode === 'manual' && !this.isMultipleCurrency) {
            // 手动模式：使用手动输入的汇率计算价格
            const manualRate = parseFloat(this.manualPrice) || 1
            newPrice = parseFloat(item.usPrice) * manualRate
          } else {
            // 自动模式：使用各自的汇率计算
            const rate = parseFloat(item.queryRate) || 1
            newPrice = parseFloat(item.usPrice) * rate
          }
          
          // 根据精度四舍五入
          if (this.selectedPrecision >= 0) {
            newPrice = parseFloat(newPrice.toFixed(this.selectedPrecision))
          } else {
            const factor = Math.pow(10, Math.abs(this.selectedPrecision))
            newPrice = Math.round(newPrice / factor) * factor
          }
          
          return {
            id: item.id,
            countryPrice: this.formatPrice(newPrice)
          }
        })
        
        // 触发批量更新事件
        this.$emit('batch-update', priceList)
        
      } catch (error) {
        console.error('批量配置失败:', error)
        this.$message.error('配置失败，请重试')
      } finally {
        this.submitting = false
      }
    },
    
    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
.batch-config-form {
  .form-item {
    margin-bottom: 24px;
    
    .form-label {
      display: inline-block;
      width: 100px;
      font-weight: 500;
      color: #303133;
    }
  }
  
  // 精度选择器
  .precision-selector {
    display: inline-flex;
    gap: 12px;
    
    .precision-option {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 8px 12px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 80px;
      
      &:hover {
        border-color: #409eff;
        background-color: #ecf5ff;
      }
      
      &.active {
        border-color: #409eff;
        background-color: #409eff;
        color: white;
      }
      
      .precision-arrow {
        font-size: 12px;
        color: #c0c4cc;
        cursor: pointer;
        
        &:hover {
          color: #409eff;
        }
      }
      
      .precision-label {
        margin: 4px 0;
        font-size: 14px;
      }
    }
  }
  
  .tip-text {
    margin-top: 8px;
    color: #e6a23c;
    font-size: 12px;
    
    i {
      margin-right: 4px;
    }
  }
  
  // 手动配置区域
  .manual-config {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 16px;
    margin-top: 12px;
    
    .currency-info {
      margin-bottom: 16px;
      
      .info-row {
        display: flex;
        margin-bottom: 8px;
        
        .info-label {
          width: 120px;
          color: #606266;
        }
        
        .info-value {
          font-weight: 500;
          color: #303133;
        }
      }
    }
    
    .manual-rate-input {
      .form-label {
        display: block;
        margin-bottom: 8px;
      }
    }
  }
  
  // 选中数据预览
  .selected-preview {
    border-top: 1px solid #ebeef5;
    padding-top: 16px;
    
    .preview-header {
      font-weight: 500;
      margin-bottom: 12px;
      color: #303133;
    }
    
    .preview-list {
      .preview-item {
        display: flex;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid #f2f6fc;
        
        .arrow {
          margin: 0 12px;
          color: #909399;
        }
        
        .new-price {
          font-weight: 500;
          color: #67c23a;
        }
      }
      
      .more-text {
        color: #909399;
        font-size: 12px;
        text-align: center;
        padding: 8px 0;
      }
    }
  }
  
  .error-text {
    color: #f56c6c;
    font-size: 12px;
    margin-top: 4px;
  }
}
</style> 